import React, { FC, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { CART_ITEM_TYPE, PAYMENT_METHOD, PaymentMethod } from "../../../../../constants";
import { DeliveryMethod, RootState, StepIndicatorType } from "../../../../../models";
import { setNegativeFlash } from "../../../app/state/generalActions";
import { trackCheckoutAndNavigate } from "../../../google-analytics";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import { useQueryParam } from "../../../components/hooks";
import ContactCard from "../../components/molecules/ContactCard/ContactCard";
import CheckoutPage from "../../components/organisms/CheckoutPage/CheckoutPage";
import DeliveryCard from "../../components/organisms/DeliveryCard/DeliveryCard";
import PaymentCard from "../../components/organisms/PaymentCard/PaymentCard";
import { usePrevious } from "../../hooks";
import { debouncedFetchDeliveryAndPaymentMethods } from "../../state/actions/combinedActions";
import * as BUSINESS_DETAILS_SELECTORS from "../../state/selectors/businessDetailsSelectors";
import { getAllCartSkus, getAllItemsOfType } from "../../state/selectors/cartSelectors";
import * as COMBINED_SELECTORS from "../../state/selectors/combinedSelectors";
import { getDeliveryInfoMethods } from "../../state/selectors/deliveryInfoSelectors";
import {
    getPaymentMethods,
    getPaymentSelectedType,
    getPaymentSuccessRedirectUrl,
} from "../../state/selectors/paymentSelectors";
import TEXT from "../../text-consts-b2b";

interface Props {
    contactEmail: string;
    contactName: string;
    contactPhone: string;
    deliveryMethods: DeliveryMethod[];
    isFetchingData: boolean;
    isHardwareOnly: boolean;
    isVoucherInCart: boolean;
    organizationAddress: string;
    organizationCity: string;
    organizationName: string;
    paymentMethods: PaymentMethod[];
    paymentSelectedType?: PaymentMethod;
    paymentSuccessRedirectUrl: string;
    skus: string[];
    stepIndicator: StepIndicatorType;
    totalPaymentPrice: number;
}

const B2BSummary: FC<Props> = ({
    organizationName,
    organizationCity,
    organizationAddress,
    contactName,
    contactPhone,
    contactEmail,
    skus,
    paymentSuccessRedirectUrl,
    isFetchingData,
    isVoucherInCart,
    totalPaymentPrice,
    deliveryMethods,
    paymentMethods,
    paymentSelectedType,
    stepIndicator,
    isHardwareOnly,
}) => {
    const stringifySkus = JSON.stringify(skus);
    const isPaymentFailed = useQueryParam("paymentFailed");
    const location = useLocation();
    const dispatch = useDispatch();

    useEffect(() => {
        // Will do several things:
        // 1) Method will retrieve all delivery methods, pick up points and shops, and also update order with a pre-selected delivery method if none exists.
        //    We want this to happen everytime either the price or the hardware skus in cart has changed.
        // 2) Retrieve payment methods and pre-selected payment method if none exists (or if one not part of new methods exists)
        dispatch(debouncedFetchDeliveryAndPaymentMethods());
    }, [debouncedFetchDeliveryAndPaymentMethods, totalPaymentPrice, stringifySkus]);

    useEffect(() => {
        // Check if paymentFailed (kept persistent since debouncedFetchDeliveryAndPaymentMethods otherwise clears it on mount)
        if (isPaymentFailed) {
            dispatch(setNegativeFlash(TEXT.ERROR.PAYMENT, undefined, true));
        }
    }, [isPaymentFailed, setNegativeFlash]);

    const prevPaymentSuccessRedirectUrl = usePrevious(paymentSuccessRedirectUrl);
    useEffect(() => {
        if (paymentSuccessRedirectUrl && paymentSuccessRedirectUrl !== prevPaymentSuccessRedirectUrl) {
            dispatch(trackCheckoutAndNavigate(2, paymentSuccessRedirectUrl));
        }
    }, [paymentSuccessRedirectUrl, prevPaymentSuccessRedirectUrl, trackCheckoutAndNavigate]);

    return (
        <CheckoutPage
            isReadyToContinue={true}
            pageHeading={TEXT.SUMMARY.HEADING}
            pageIndex={2}
            isFetchingData={isFetchingData}
            stepIndicator={stepIndicator}
            path={location.pathname}
        >
            {isHardwareOnly && (
                <ContactCard
                    headingText={`${TEXT.SUMMARY.RECEIVER}:`}
                    name={organizationName}
                    address={organizationAddress}
                    city={organizationCity}
                    contactName={contactName}
                    contactPhone={contactPhone}
                    contactEmail={contactEmail}
                    showContactInfo={true}
                />
            )}
            {deliveryMethods.length > 0 && <DeliveryCard />}
            {((paymentMethods.length > 0 && totalPaymentPrice > 0) ||
                paymentSelectedType === PAYMENT_METHOD.INVOICE ||
                isVoucherInCart) && <PaymentCard />}
        </CheckoutPage>
    );
};

const mapStateToProps = (state: RootState): Props => ({
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    skus: getAllCartSkus(state),
    totalPaymentPrice: COMBINED_SELECTORS.getTotalPaymentPrice(state),
    isVoucherInCart: getAllItemsOfType(state, CART_ITEM_TYPE.VOUCHER).length > 0,
    organizationName: BUSINESS_DETAILS_SELECTORS.getOrganizationName(state),
    organizationCity: BUSINESS_DETAILS_SELECTORS.getOrganizationFullCity(state),
    organizationAddress: BUSINESS_DETAILS_SELECTORS.getOrganizationAddress(state),
    contactName: BUSINESS_DETAILS_SELECTORS.getContactFullName(state),
    contactPhone: BUSINESS_DETAILS_SELECTORS.getContactPhone(state),
    contactEmail: BUSINESS_DETAILS_SELECTORS.getContactEmail(state),
    paymentSuccessRedirectUrl: getPaymentSuccessRedirectUrl(state),
    deliveryMethods: getDeliveryInfoMethods(state),
    paymentMethods: getPaymentMethods(state),
    paymentSelectedType: getPaymentSelectedType(state),
    stepIndicator: BUSINESS_DETAILS_SELECTORS.getStepIndicatorData(),
    isHardwareOnly: COMBINED_SELECTORS.isHardwareOnly(state),
});

export default connect(mapStateToProps)(B2BSummary);
